import { z } from 'zod';

import { contextModeSchema } from './contextMode';

export const supportForkStatusSchema = z.enum(['available', 'in_progress', 'unavailable']);

/**
 * Which agent role answers a conversation. `questions` serves an organization
 * until it connects a ticketing system and has no report tools;
 * `investigations` serves once it does. A role this build does not know reads
 * as null, the same as a conversation from before roles were reported.
 */
export const supportAgentServingRoleSchema = z.enum(['questions', 'investigations']);

export type SupportAgentServingRole = z.infer<typeof supportAgentServingRoleSchema>;

export const supportTicketComplexitySchema = z.object({
  assessed_at: z.iso.datetime({ offset: true }),
  reason: z.string(),
  size: z.enum(['small', 'medium', 'large']),
});

export const supportConversationSchema = z.object({
  // Optional so a server that predates ownerless conversations still parses.
  anonymous: z.boolean().optional(),
  caller_fork_id: z.guid().nullable().optional(),
  // Optional so a server that predates complexity scoring still parses; null
  // means the ticket has no scored complexity, and the UI shows nothing for
  // it. A shape this build does not know (a new size, say) also reads as null
  // rather than failing the whole list.
  complexity: supportTicketComplexitySchema.nullable().optional().catch(null),
  continuable: z.boolean(),
  continuable_until: z.iso.datetime({ offset: true }).nullable(),
  created_at: z.iso.datetime({ offset: true }),
  fork_status: supportForkStatusSchema.nullable().optional(),
  id: z.guid(),
  last_turn_at: z.iso.datetime({ offset: true }).nullable(),
  // Only the list fills this; the single-conversation GET carries the transcript instead.
  last_message_preview: z.string().nullable().optional(),
  owned_by_caller: z.boolean().optional(),
  serving_role: supportAgentServingRoleSchema.nullable().optional().catch(null),
  status: z.enum(['active', 'waiting_for_input', 'closed']),
  ticket_id: z.string().nullable(),
  title: z.string().nullable(),
});

export type SupportConversation = z.infer<typeof supportConversationSchema>;

export const supportConversationListSchema = z.object({
  conversations: z.array(supportConversationSchema),
});

export type SupportConversationList = z.infer<typeof supportConversationListSchema>;

/**
 * One entry of `toolCalls` on a persisted assistant row. The row schema keeps
 * the array as `unknown[]`; only the name is read, to classify the row, so a
 * malformed entry is skipped rather than failing the whole history.
 */
export const persistedToolCallSchema = z
  .object({
    function: z.object({ name: z.string() }).loose(),
  })
  .loose();

export const supportAttachmentKindSchema = z.enum(['image', 'pdf', 'spreadsheet', 'csv', 'text', 'markdown']);

export type SupportAttachmentKind = z.infer<typeof supportAttachmentKindSchema>;

/**
 * One file attached to a user message, as the upload endpoint and the
 * conversation GET both describe it. `url` is a short-lived signed link the
 * server adds for images only, fresh on every read; nothing else is ever a
 * byte of the file itself.
 */
export const supportAttachmentSchema = z
  .object({
    content_type: z.string(),
    filename: z.string(),
    id: z.guid(),
    kind: supportAttachmentKindSchema,
    page_count: z.number().int().nullable().optional(),
    sheet_names: z.array(z.string()).nullable().optional(),
    size_bytes: z.number().int(),
    source: z.enum(['chat_upload', 'ticket']),
    source_ref: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
    url_expires_at: z.iso.datetime({ offset: true }).nullable().optional(),
  })
  .loose();

export type SupportAttachment = z.infer<typeof supportAttachmentSchema>;

export const supportMessageSchema = z
  .object({
    attachments: z.array(supportAttachmentSchema).optional(),
    content: z.string().nullable().optional(),
    id: z.string(),
    role: z.enum(['user', 'assistant', 'tool']),
    toolCallId: z.string().optional(),
    toolCalls: z.array(z.unknown()).optional(),
  })
  .loose();

export type SupportMessage = z.infer<typeof supportMessageSchema>;

export const supportMessageMetaSchema = z.object({
  created_at: z.iso.datetime({ offset: true }),
  id: z.string(),
  status: z.enum(['complete', 'partial', 'error']),
});

export type SupportMessageMeta = z.infer<typeof supportMessageMetaSchema>;

export const supportConversationDetailSchema = z
  .object({
    conversation: supportConversationSchema,
    message_meta: z.array(supportMessageMetaSchema),
    messages: z.array(supportMessageSchema),
    report: z.string().nullable(),
    report_updated_at: z.iso.datetime({ offset: true }).nullable(),
  })
  .superRefine((detail, context) => {
    if (detail.messages.length !== detail.message_meta.length) {
      context.addIssue({
        code: 'custom',
        message: 'messages and message_meta must have matching lengths',
      });
      return;
    }

    detail.messages.forEach((message, index) => {
      if (message.id !== detail.message_meta[index].id) {
        context.addIssue({
          code: 'custom',
          message: `message metadata at index ${index} does not match its message`,
        });
      }
    });
  });

export type SupportConversationDetail = z.infer<typeof supportConversationDetailSchema>;

export const supportConversationFeedbackSchema = z.object({
  comment: z.string().nullable(),
  conversation_id: z.guid(),
  created_at: z.iso.datetime({ offset: true }),
  id: z.guid(),
  /** An opaque key. The server stores whatever it is sent and enumerates
   * nothing, so the choice set lives entirely on this side — see
   * `components/Investigations/lib/feedback.ts`. */
  rating: z.string(),
});

export type SupportConversationFeedback = z.infer<typeof supportConversationFeedbackSchema>;

export const supportConversationFeedbackListSchema = z.object({
  feedback: z.array(supportConversationFeedbackSchema),
});

export type SupportConversationFeedbackList = z.infer<typeof supportConversationFeedbackListSchema>;

export const supportAgentInstanceStatusSchema = z.enum([
  'needs_setup',
  'provisioning',
  'ready',
  'needs_attention',
  'degraded',
  'capacity',
  'deleting',
  'deleted',
]);

export type SupportAgentInstanceStatus = z.infer<typeof supportAgentInstanceStatusSchema>;

/**
 * Whether, and through which role, the support agent can answer the caller's
 * organization. Every field is optional so a server that predates one still
 * parses; a value this build does not know reads as unknown rather than
 * failing the whole answer.
 */
export const supportAgentReadinessSchema = z.object({
  // The organization's context mode, the same value `/me` carries; absent on
  // a server from before the mode. `useContextMode` is what the UI reads.
  context_mode: contextModeSchema.optional().catch(undefined),
  flag_enabled: z.boolean().optional(),
  // Some codebase has a version with its source on hand: connected, or any
  // status past it. Resolved into `has_readable_codebase` by the server.
  has_connected_codebase: z.boolean().optional(),
  // A GENERATION_COMPLETE version exists: the onboarding checklist's
  // context-ready step, and what a server from before the mode reports.
  has_context_ready_codebase: z.boolean().optional(),
  // The one to switch on: the organization has a codebase the agent's tools
  // can read in its mode, `has_connected_codebase` outside LEGACY and
  // `has_context_ready_codebase` in it. The server resolves the mode so the
  // UI never re-derives it. Absent on a server from before the mode.
  has_readable_codebase: z.boolean().optional(),
  // While nothing serves, these describe the instance that would serve first.
  instance_status: supportAgentInstanceStatusSchema.nullable().optional().catch(null),
  serving_role: z
    .union([z.literal('none'), supportAgentServingRoleSchema])
    .optional()
    .catch('none'),
  // Only ever one of converge's setup reasons; null for anything else.
  status_message: z.string().nullable().optional(),
  // A ticketing credential is saved. Absent on a server from before it said.
  ticketing_connected: z.boolean().optional(),
});

export type SupportAgentReadiness = z.infer<typeof supportAgentReadinessSchema>;

/**
 * Whether the organization has a codebase the agent's tools can read in its
 * context mode. A server from before the mode says only whether context is
 * ready, which is the LEGACY answer, the only mode such a server serves.
 */
export function hasReadableCodebase(readiness: SupportAgentReadiness | undefined): boolean {
  return readiness?.has_readable_codebase ?? readiness?.has_context_ready_codebase ?? false;
}

export const supportAgentErrorDetailSchema = z.object({
  code: z.string(),
  context: z.record(z.string(), z.unknown()).default({}),
  message: z.string(),
});

export const supportAgentErrorSchema = z.object({
  detail: supportAgentErrorDetailSchema,
});

const runStartedEventSchema = z.object({
  runId: z.string(),
  threadId: z.string(),
  type: z.literal('RUN_STARTED'),
});

const textMessageStartEventSchema = z.object({
  messageId: z.string(),
  role: z.string(),
  type: z.literal('TEXT_MESSAGE_START'),
});

const textMessageContentEventSchema = z.object({
  delta: z.string(),
  messageId: z.string(),
  type: z.literal('TEXT_MESSAGE_CONTENT'),
});

const textMessageEndEventSchema = z.object({
  messageId: z.string(),
  type: z.literal('TEXT_MESSAGE_END'),
});

const toolCallStartEventSchema = z
  .object({
    parentMessageId: z.string().optional(),
    toolCallId: z.string(),
    toolCallName: z.string(),
    type: z.literal('TOOL_CALL_START'),
  })
  .loose();

const toolCallArgsEventSchema = z.object({
  delta: z.string(),
  toolCallId: z.string(),
  type: z.literal('TOOL_CALL_ARGS'),
});

const toolCallEndEventSchema = z.object({
  toolCallId: z.string(),
  type: z.literal('TOOL_CALL_END'),
});

const toolCallResultEventSchema = z
  .object({
    content: z.string(),
    messageId: z.string(),
    role: z.string(),
    toolCallId: z.string(),
    type: z.literal('TOOL_CALL_RESULT'),
  })
  .loose();

const customEventSchema = z
  .object({
    name: z.string(),
    type: z.literal('CUSTOM'),
    value: z.unknown(),
  })
  .loose();

/**
 * The `CUSTOM` payloads the client folds. `view` stays a plain string on the
 * wire; the adapter narrows it, so a view this build does not know folds
 * nothing rather than failing the parse.
 */
export const saveReportValueSchema = z.object({
  content: z.string(),
  updated_at: z.iso.datetime({ offset: true }),
});

export const postReportViewValueSchema = z.object({
  content: z.string(),
  view: z.string(),
});

const runFinishedEventSchema = z.object({
  runId: z.string(),
  threadId: z.string(),
  type: z.literal('RUN_FINISHED'),
});

const runErrorEventSchema = z
  .object({
    code: z.string(),
    message: z.string(),
    type: z.literal('RUN_ERROR'),
  })
  .loose();

export const supportAgentEventSchema = z.discriminatedUnion('type', [
  runStartedEventSchema,
  textMessageStartEventSchema,
  textMessageContentEventSchema,
  textMessageEndEventSchema,
  toolCallStartEventSchema,
  toolCallArgsEventSchema,
  toolCallEndEventSchema,
  toolCallResultEventSchema,
  customEventSchema,
  runFinishedEventSchema,
  runErrorEventSchema,
]);

export type SupportAgentEvent = z.infer<typeof supportAgentEventSchema>;

export interface SupportAgentRunInput {
  context: unknown[];
  forwardedProps: Record<string, unknown>;
  messages: {
    /** Uploads this message carries, by id; the server binds them to the stored row. */
    attachments?: { id: string }[];
    content: string;
    id: string;
    role: 'user';
  }[];
  runId: string;
  threadId: string;
  tools: unknown[];
}
